import React, { useState, useEffect, useContext, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Snackbar,
  Alert,
  Slide,
} from '@mui/material';
import {
  NotificationsActive,
  Close,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  isPushSupported,
  isUserAuthenticated,
  registerServiceWorker,
  getVapidPublicKey,
} from '../utils/pushNotifications';
import { AuthContext } from '../context/AuthContext';
import api from '../utils/api';

const MotionPaper = motion(Paper);

// Convert VAPID key to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// How often to re-show the prompt (in milliseconds) - 1 hour
const PROMPT_COOLDOWN_MS = 60 * 60 * 1000;

/**
 * Push Notification Prompt - Shows a prompt to enable push notifications
 * - Shows after 3 seconds if user is logged in and notifications not enabled
 * - Dismisses on enable (success or error), "Later", or "Don't ask again"
 * - "Later" sets a 1-hour cooldown; "Don't ask again" permanently dismisses
 * - No polling — checks once on mount
 */
function PushNotificationPrompt() {
  const { isAuthenticated } = useContext(AuthContext);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  const userDismissed = useRef(false);
  const isSupported = isPushSupported();

  // Single check on mount + auth change — no polling
  useEffect(() => {
    if (!isSupported || !isAuthenticated || userDismissed.current) return;

    let cancelled = false;

    async function checkAndShow() {
      console.log('[PushPrompt] checkAndShow running...');
      console.log('[PushPrompt] Permission:', Notification.permission);

      // Cooldown check
      const lastAsked = localStorage.getItem('pushPromptLastAsked');
      if (lastAsked) {
        const timeSinceAsked = Date.now() - parseInt(lastAsked);
        console.log('[PushPrompt] Cooldown remaining:', Math.round((PROMPT_COOLDOWN_MS - timeSinceAsked) / 1000), 'seconds');
        if (timeSinceAsked < PROMPT_COOLDOWN_MS) {
          console.log('[PushPrompt] Skipped — cooldown active');
          return;
        }
      }

      // Note: even if permission is 'denied', we still show the prompt
      // so the user knows they need to enable it in browser settings

      // Already subscribed in browser — no need to prompt
      if (Notification.permission === 'granted') {
        try {
          const registration = await navigator.serviceWorker.ready;
          const subscription = await registration.pushManager.getSubscription();
          if (subscription) {
            console.log('[PushPrompt] Skipped — already subscribed');
            return;
          }
          console.log('[PushPrompt] Permission granted but no subscription — will show prompt');
        } catch {
          // Ignore — will show prompt
        }
      }

      // Show prompt after 3 seconds
      if (!cancelled && !userDismissed.current) {
        console.log('[PushPrompt] Will show prompt in 3 seconds');
        setTimeout(() => {
          if (!cancelled && !userDismissed.current) {
            console.log('[PushPrompt] Showing prompt now');
            setVisible(true);
          }
        }, 3000);
      }
    }

    checkAndShow();
    return () => { cancelled = true; };
  }, [isSupported, isAuthenticated]);

  // Request permission and subscribe — ALL paths dismiss the prompt
  const handleEnable = async () => {
    console.log('[Push] Enable clicked, requesting permission IMMEDIATELY...');

    if (!isAuthenticated || !isUserAuthenticated()) {
      setSnackbar({
        open: true,
        message: 'Please log in to enable notifications.',
        severity: 'warning',
      });
      userDismissed.current = true;
      setVisible(false);
      return;
    }

    // Step 1: Request permission FIRST — must be the first async call on user gesture
    let permissionResult = Notification.permission;
    if (permissionResult === 'default') {
      try {
        permissionResult = await Notification.requestPermission();
        console.log('[Push] Permission result:', permissionResult);
      } catch (err) {
        console.error('[Push] Permission request failed:', err);
        setSnackbar({
          open: true,
          message: 'Failed to request permission: ' + err.message,
          severity: 'error',
        });
        userDismissed.current = true;
        setVisible(false);
        return;
      }
    }

    if (permissionResult !== 'granted') {
      setSnackbar({
        open: true,
        message: 'Notifications blocked. Enable them in your browser settings.',
        severity: 'warning',
      });
      localStorage.setItem('pushPromptLastAsked', Date.now().toString());
      userDismissed.current = true;
      setVisible(false);
      return;
    }

    // Step 2: Subscribe
    setLoading(true);

    try {
      const vapidKey = await getVapidPublicKey();
      if (!vapidKey) throw new Error('Push notifications not configured');

      const registration = await registerServiceWorker();
      if (!registration) throw new Error('Service worker registration failed');

      await navigator.serviceWorker.ready;

      let subscription = await registration.pushManager.getSubscription();
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidKey),
        });
      }

      // Save to server
      const subscriptionJSON = subscription.toJSON();
      await api.post('/push/subscribe', {
        subscription: {
          endpoint: subscriptionJSON.endpoint,
          keys: {
            p256dh: subscriptionJSON.keys.p256dh,
            auth: subscriptionJSON.keys.auth,
          },
        },
      });

      // Clear cooldowns on success
      sessionStorage.removeItem('pushPromptDismissedAt');
      localStorage.removeItem('pushPromptLastAsked');

      setSnackbar({
        open: true,
        message: 'Notifications enabled! You\'ll now receive updates.',
        severity: 'success',
      });
    } catch (error) {
      console.error('[Push] Subscription error:', error);
      setSnackbar({
        open: true,
        message: error.message || 'Failed to enable notifications',
        severity: 'error',
      });
      localStorage.setItem('pushPromptLastAsked', Date.now().toString());
    } finally {
      setLoading(false);
      userDismissed.current = true;
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    userDismissed.current = true;
    setVisible(false);
    localStorage.setItem('pushPromptLastAsked', Date.now().toString());
  };

  if (!isSupported || !isAuthenticated) {
    return null;
  }

  return (
    <>
      <AnimatePresence>
        {visible && (
          <MotionPaper
            initial={{ opacity: 0, y: 100, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 100, scale: 0.95 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: { xs: 80, md: 24 },
              left: { xs: 16, md: 'auto' },
              right: { xs: 16, md: 24 },
              maxWidth: { xs: '100%', md: 400 },
              zIndex: 1300,
              borderRadius: 3,
              overflow: 'hidden',
            }}
          >
            {/* Gradient header */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #F26522 0%, #F7B32B 100%)',
                p: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <NotificationsActive sx={{ color: 'white', fontSize: 28 }} />
              <Typography variant="subtitle1" fontWeight="700" color="white" flex={1}>
                Stay Updated
              </Typography>
              <IconButton
                size="small"
                onClick={handleDismiss}
                sx={{ color: 'rgba(255,255,255,0.8)' }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 2.5 }}>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Get instant updates on your bookings, new leads, and important announcements.
              </Typography>

              <Box display="flex" gap={1.5}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleEnable}
                  disabled={loading}
                  sx={{
                    background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)',
                    fontWeight: 600,
                    textTransform: 'none',
                    borderRadius: 2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #FF8A50 0%, #F26522 100%)',
                    },
                  }}
                >
                  {loading ? 'Enabling...' : 'Enable Notifications'}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDismiss}
                  sx={{
                    textTransform: 'none',
                    borderRadius: 2,
                    minWidth: 'auto',
                    px: 2,
                  }}
                >
                  Later
                </Button>
              </Box>

            </Box>
          </MotionPaper>
        )}
      </AnimatePresence>

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        TransitionComponent={Slide}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PushNotificationPrompt;
